/**
 * VoicePanel — Voice calibration configuration.
 *
 * Settings for how the AI learns and reproduces the creator's writing style.
 * Per-project calibration status display.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api/engine';
import { SettingsSection, UpgradeNudge, SliderSetting, DropdownSetting } from '@seitiate/settings-ui';

interface VoiceSettings {
    calibration_strategy: string;
    ensemble_size: number;
    drift_tolerance: number;
}

export const VoicePanel: React.FC<{ mode: 'admin' | 'tenant' }> = () => {
    const [visibleCategories, setVisibleCategories] = useState<string[]>([]);
    const [voice, setVoice] = useState<VoiceSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const overview = await api.settings.getOverview();
            setVisibleCategories(overview.visible_categories || []);
            if (overview.visible_categories.includes('voice')) {
                const voiceCategory = await api.settings.getCategory('voice').catch(() => null);
                if (voiceCategory?.settings) {
                    setVoice(voiceCategory.settings as unknown as VoiceSettings);
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const updateVoice = async (key: string, value: number | string) => {
        if (!voice) return;
        setVoice({ ...voice, [key]: value });
        try {
            const response = await api.settings.updateCategory('voice', { [key]: value });
            setVoice(response.settings as unknown as VoiceSettings);
        } catch {
            setFeedback('Failed to save');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500 font-mono text-sm animate-pulse">Loading voice config...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-400 font-mono text-sm">{error}</div>
            </div>
        );
    }

    const hasVoice = visibleCategories.includes('voice');

    return (
        <div className="space-y-4">
            <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">
                Voice :: Calibration
            </h3>

            {feedback && (
                <div className="text-[10px] font-mono text-[#39FF14]">{feedback}</div>
            )}

            {hasVoice && voice ? (
                <SettingsSection title="Voice Settings">
                    <div className="text-[9px] font-mono text-[#39FF14]/70 mb-1">
                        These controls now steer the live calibration path, breadth, and drift tolerance.
                    </div>
                    <DropdownSetting
                        label="Calibration Strategy"
                        value={voice.calibration_strategy}
                        options={[
                            { value: 'tournament', label: 'Tournament (recommended)' },
                            { value: 'single-shot', label: 'Single-Shot' },
                            { value: 'manual', label: 'Manual' },
                        ]}
                        onChange={(v) => updateVoice('calibration_strategy', v)}
                        description="Tournament generates multiple variants and picks the best match"
                    />

                    <SliderSetting
                        label="Ensemble Size"
                        value={voice.ensemble_size}
                        min={1}
                        max={5}
                        onChange={(v) => updateVoice('ensemble_size', v)}
                        description="Number of voice models in the calibration ensemble"
                    />

                    <SliderSetting
                        label="Drift Tolerance"
                        value={voice.drift_tolerance}
                        min={0.05}
                        max={0.30}
                        step={0.01}
                        onChange={(v) => updateVoice('drift_tolerance', v)}
                        formatValue={(v) => v.toFixed(2)}
                        description="Maximum voice drift before recalibration triggers"
                    />
                </SettingsSection>
            ) : (
                <UpgradeNudge requiredTier="Making" feature="voice calibration settings" />
            )}

            {/* Per-Project Calibration Info */}
            <SettingsSection title="Calibration Status" defaultExpanded={false}>
                <div className="bg-gray-900/40 rounded p-3 text-center">
                    <div className="text-[10px] text-gray-500 font-mono">
                        Voice calibration runs per-project during the creation flow.
                    </div>
                    <div className="text-[10px] text-gray-600 mt-1">
                        Create a project and start writing to begin voice calibration.
                    </div>
                </div>
            </SettingsSection>
        </div>
    );
};
